import { Flex, Paper, Title, Loader, Text, Center, Stack } from "@mantine/core";
import React, { useCallback, useMemo, useState } from "react";
import { FcDepartment } from "react-icons/fc";
import { MdErrorOutline } from "react-icons/md";
import "./DepartmentConnectionCard.scss";
import MeetingDistModal from "components/Insights/MeetingDistBarCard/MeetingDistModal";
import { useQuery } from "react-query";
import { getMeetingsForMyOrg } from "api";

const DepartmentConnectionCard = ({
  data,
  isLoading,
  isError,
  uid,
  duration,
}) => {
  const [hoveredDept, setHoveredDept] = useState(null);
  const [hoveredLine, setHoveredLine] = useState(null);
  const [modalOpened, setModalOpened] = useState(false);
  const [modalData, setModalData] = useState(null);

  const redisKey = useMemo(() => {
    return `users:${uid}:orgCalculations:departmentCollaboration:${duration}:all:${modalData?.source}|${modalData?.target}`;
  }, [uid, duration, modalData]);

  // Fetch meeting details using useQuery
  const {
    data: meetingDetails,
    isLoading: isLoadingMeetings,
    isError: isErrorMeetings,
    error: meetingError,
  } = useQuery(
    ["departmentMeetingDetails", redisKey],
    () => getMeetingsForMyOrg(redisKey),
    {
      enabled: modalOpened && redisKey !== null,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    }
  );

  // Handle loading state
  if (isLoading) {
    return (
      <Wrapper>
        <Center style={{ minHeight: "350px" }}>
          <Stack align="center" spacing="md">
            <Loader size="lg" variant="dots" />
            <Text size="sm" color="dimmed">
              Loading department connections...
            </Text>
          </Stack>
        </Center>
      </Wrapper>
    );
  }

  // Handle error state
  if (isError) {
    return (
      <Wrapper>
        <Center style={{ minHeight: "350px" }}>
          <Stack align="center" spacing="md">
            <MdErrorOutline size={48} color="#ef4444" />
            <Text size="sm" color="red" weight={500}>
              Failed to load department connections
            </Text>
            <Text size="xs" color="dimmed">
              Please try again later
            </Text>
          </Stack>
        </Center>
      </Wrapper>
    );
  }

  // Handle empty data state
  if (
    !data ||
    !data.departments ||
    !data.interactionData ||
    data.departments.length === 0
  ) {
    return (
      <Wrapper>
        <Center style={{ minHeight: "350px" }}>
          <Stack align="center" spacing="md">
            <FcDepartment size={48} />
            <Text size="sm" color="dimmed">
              No department interaction data available
            </Text>
          </Stack>
        </Center>
      </Wrapper>
    );
  }

  const { departments, interactionData } = data;

  // Calculate department statistics
  const deptStats = departments
    .map((dept) => {
      const outgoing = interactionData.filter((d) => d.source === dept);
      const incoming = interactionData.filter((d) => d.target === dept);
      const totalMeetings =
        outgoing.reduce((sum, d) => sum + d.meetings, 0) +
        incoming.reduce((sum, d) => sum + d.meetings, 0);
      const uniqueConnections = new Set([
        ...outgoing.map((d) => d.target),
        ...incoming.map((d) => d.source),
      ]).size;

      return {
        name: dept,
        meetings: totalMeetings,
        connections: uniqueConnections,
        isolation:
          uniqueConnections < 3
            ? "High"
            : uniqueConnections < 5
            ? "Medium"
            : "Low",
      };
    })
    .sort((a, b) => b.meetings - a.meetings);

  // Network view - simplified force-directed concept
  const networkNodes = departments.map((dept, i) => {
    const angle = (i / departments.length) * 2 * Math.PI;
    const radius = 35;
    const stat = deptStats.find((s) => s.name === dept);
    return {
      name: dept,
      x: 50 + radius * Math.cos(angle),
      y: 50 + radius * Math.sin(angle),
      meetings: stat?.meetings || 0,
      connections: stat?.connections || 0,
    };
  });

  const getColor = (value, max) => {
    if (max === 0) return "#e5e7eb";
    const intensity = value / max;
    if (intensity > 0.7) return "#ef4444";
    if (intensity > 0.4) return "#f59e0b";
    if (intensity > 0.2) return "#10b981";
    return "#e5e7eb";
  };

  const maxMeetings = Math.max(...deptStats.map((d) => d.meetings), 1);
  return (
    <Wrapper>
      <svg
        width="100%"
        height="450"
        viewBox="0 0 100 100"
        className="border border-slate-200 rounded-lg"
        style={{
          transition: "all 0.3s ease",
        }}
      >
        {/* Draw connections first (as background) */}
        {interactionData.map((link, i) => {
          const sourceNode = networkNodes.find((n) => n.name === link.source);
          const targetNode = networkNodes.find((n) => n.name === link.target);

          // Skip if nodes not found
          if (!sourceNode || !targetNode) return null;

          const isLineHovered = hoveredLine === i;
          const baseStrokeWidth = (link.meetings / 50) * 0.8;
          const strokeWidth = isLineHovered
            ? baseStrokeWidth * 2.5
            : baseStrokeWidth;
          const opacity = isLineHovered
            ? 0.9
            : 0.2 + (link.meetings / 50) * 0.5;

          // Calculate midpoint for label
          const midX = (sourceNode.x + targetNode.x) / 2;
          const midY = (sourceNode.y + targetNode.y) / 2;

          // Show numbers for significant connections (10+ meetings) or when hovered
          const showNumber = link.meetings >= 10 || isLineHovered;
          const numberScale = isLineHovered ? 1.3 : 1;
          const circleBgRadius = isLineHovered ? 2 : 1.5;

          //   const handleLineClick = () => {
          //     console.log("Connection Data:", {
          //       source: link.source,
          //       target: link.target,
          //       meetings: link.meetings,
          //       hours: link.hours,
          //       fullData: link
          //     });
          //   };

          const handleLineClick = () => {
            setModalData({
              source: link.source,
              target: link.target,
              name: `${link.source} - ${link.target} meetings`,
              type: "Department Collaboration",
            });
            setModalOpened(true);
          };

          return (
            <g key={i}>
              {/* Actual visible line */}
              <line
                x1={sourceNode.x}
                y1={sourceNode.y}
                x2={targetNode.x}
                y2={targetNode.y}
                stroke="#3b82f6"
                strokeWidth={strokeWidth}
                opacity={opacity}
                style={{
                  transition: "stroke-width 0.3s ease, opacity 0.3s ease",
                  pointerEvents: "none",
                }}
              />
              {/* Invisible wider line for better hover/click area */}
              <line
                x1={sourceNode.x}
                y1={sourceNode.y}
                x2={targetNode.x}
                y2={targetNode.y}
                stroke="transparent"
                strokeWidth="2"
                style={{ cursor: "pointer" }}
                onMouseEnter={() => setHoveredLine(i)}
                onMouseLeave={() => setHoveredLine(null)}
                onClick={handleLineClick}
              />
              {showNumber && (
                <g
                  style={{
                    transform: `scale(${numberScale})`,
                    transformOrigin: `${midX}px ${midY}px`,
                    transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                >
                  <circle
                    cx={midX}
                    cy={midY}
                    r={circleBgRadius}
                    fill="white"
                    opacity="0.95"
                    style={{
                      transition: "r 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  />
                  <text
                    x={midX}
                    y={midY + 0.4}
                    textAnchor="middle"
                    fontSize="1.8"
                    fill="#1e293b"
                    fontWeight={isLineHovered ? "700" : "600"}
                    className="pointer-events-none"
                    style={{
                      transition:
                        "font-weight 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    {link.meetings}
                  </text>
                </g>
              )}
            </g>
          );
        })}

        {/* Draw nodes with different shapes */}
        {networkNodes.map((node, i) => {
          const size = 2 + (node.meetings / maxMeetings) * 3;
          const isHovered = hoveredDept === node.name;
          const color = getColor(node.meetings, maxMeetings);

          // Different shapes based on department type
          const getShape = (name) => {
            if (name.includes("Engineering") || name.includes("Operations"))
              return "square";
            if (name.includes("Sales") || name.includes("Marketing"))
              return "diamond";
            if (name.includes("Product")) return "hexagon";
            if (name.includes("Finance") || name.includes("HR"))
              return "triangle";
            return "circle";
          };

          const shape = getShape(node.name);

          return (
            <g
              key={i}
              style={{
                transform: isHovered ? "scale(1.2)" : "scale(1)",
                transformOrigin: `${node.x}% ${node.y}%`,
                transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              {shape === "circle" && (
                <circle
                  cx={node.x}
                  cy={node.y}
                  r={size}
                  fill={color}
                  stroke="white"
                  strokeWidth="0.4"
                  className="cursor-pointer"
                  onMouseEnter={() => setHoveredDept(node.name)}
                  onMouseLeave={() => setHoveredDept(null)}
                />
              )}
              {shape === "square" && (
                <rect
                  x={node.x - size}
                  y={node.y - size}
                  width={size * 2}
                  height={size * 2}
                  fill={color}
                  stroke="white"
                  strokeWidth="0.4"
                  className="cursor-pointer"
                  onMouseEnter={() => setHoveredDept(node.name)}
                  onMouseLeave={() => setHoveredDept(null)}
                />
              )}
              {shape === "diamond" && (
                <polygon
                  points={`${node.x},${node.y - size * 1.4} ${
                    node.x + size * 1.4
                  },${node.y} ${node.x},${node.y + size * 1.4} ${
                    node.x - size * 1.4
                  },${node.y}`}
                  fill={color}
                  stroke="white"
                  strokeWidth="0.4"
                  className="cursor-pointer"
                  onMouseEnter={() => setHoveredDept(node.name)}
                  onMouseLeave={() => setHoveredDept(null)}
                />
              )}
              {shape === "triangle" && (
                <polygon
                  points={`${node.x},${node.y - size * 1.3} ${
                    node.x + size * 1.15
                  },${node.y + size * 0.65} ${node.x - size * 1.15},${
                    node.y + size * 0.65
                  }`}
                  fill={color}
                  stroke="white"
                  strokeWidth="0.4"
                  className="cursor-pointer"
                  onMouseEnter={() => setHoveredDept(node.name)}
                  onMouseLeave={() => setHoveredDept(null)}
                />
              )}
              {shape === "hexagon" && (
                <polygon
                  points={`${node.x},${node.y - size * 1.2} ${
                    node.x + size * 1.04
                  },${node.y - size * 0.6} ${node.x + size * 1.04},${
                    node.y + size * 0.6
                  } ${node.x},${node.y + size * 1.2} ${node.x - size * 1.04},${
                    node.y + size * 0.6
                  } ${node.x - size * 1.04},${node.y - size * 0.6}`}
                  fill={color}
                  stroke="white"
                  strokeWidth="0.4"
                  className="cursor-pointer"
                  onMouseEnter={() => setHoveredDept(node.name)}
                  onMouseLeave={() => setHoveredDept(null)}
                />
              )}

              <text
                x={node.x}
                y={node.y < 50 ? node.y - size - 1.5 : node.y + size + 2.5}
                textAnchor="middle"
                fontSize="2.5"
                fill="#1e293b"
                fontWeight={isHovered ? "bold" : "normal"}
                className="pointer-events-none"
                style={{
                  transition: "font-weight 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              >
                {node.name}
              </text>
              {isHovered && (
                <text
                  x={node.x}
                  y={node.y < 50 ? node.y - size - 3.5 : node.y + size + 4.5}
                  textAnchor="middle"
                  fontSize="2"
                  fill="#64748b"
                  className="pointer-events-none"
                  style={{
                    animation: "fadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                >
                  {node.meetings} meetings • {node.connections} connections
                </text>
              )}
            </g>
          );
        })}
      </svg>
      <MeetingDistModal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        data={modalData}
        meetingDetails={meetingDetails}
        isLoadingMeetings={isLoadingMeetings}
        isErrorMeetings={isErrorMeetings}
        meetingError={meetingError}
      />
    </Wrapper>
  );
};

export default DepartmentConnectionCard;

const Wrapper = ({ children }) => {
  return (
    <Paper
      shadow="sm"
      p="md"
      radius="md"
      withBorder
      className="card"
      style={{ overflow: "visible" }}
      mih={"20rem"}
    >
      <Flex direction="column" sx={{ width: "100%", height: "100%" }}>
        <Flex align="center">
          <FcDepartment size={18} style={{ marginRight: 8 }} />
          <Title order={5}>Departments Collaboration</Title>
        </Flex>
        <Flex direction="column" style={{ flex: 1 }}>
          {children}
        </Flex>
      </Flex>
    </Paper>
  );
};
